import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useState,
  forwardRef,
} from "react";

type SimpleBarReactExports = typeof import("simplebar-react");
type SimpleBarComponent = SimpleBarReactExports["default"];
type SimpleBarProps = React.ComponentProps<SimpleBarComponent>;

let cachedModule: Promise<SimpleBarReactExports> | null = null;
let cssInjected = false;

const loadSimpleBar = (): Promise<SimpleBarReactExports> => {
  if (cachedModule) return cachedModule;
  cachedModule = import(
    /* webpackChunkName: "simplebar" */ "simplebar-react"
  ).then((mod) => {
    if (!cssInjected && typeof document !== "undefined") {
      cssInjected = true;
      // Charge le CSS simplebar en même temps que le JS, en lazy.
      // On utilise un import CSS dynamique pour que webpack l'extrait dans le
      // même chunk que simplebar-react.
      import(
        /* webpackChunkName: "simplebar" */ "simplebar-react/dist/simplebar.min.css"
      );
    }
    return mod;
  });
  return cachedModule;
};

export interface ILazySimpleBarProps extends Omit<SimpleBarProps, "ref"> {
  /** Hauteur max du conteneur, identique à ce que SimpleBar gérait. */
  style?: CSSProperties;
  children?: ReactNode;
  className?: string;
}

/**
 * Drop-in pour `SimpleBar` de `simplebar-react`.
 *
 * Pourquoi : `simplebar-core` (+ styles) pèse ~40 KB gzip et était hoist dans
 * le chunk commons via `OptionList`/`CarteDropdown` qui sont chargés dès le
 * header. Ce wrapper rend d'abord un `<div>` natif avec `overflow: auto` qui
 * offre le même comportement de scroll (sans la scrollbar custom stylée).
 * Après le montage (donc après LCP/TTI), il charge `simplebar-react` et bascule
 * dessus pour retrouver le rendu custom.
 */
const LazySimpleBar = forwardRef<HTMLElement, ILazySimpleBarProps>(
  ({ children, style, className, ...rest }, ref) => {
    const [SimpleBarComp, setSimpleBarComp] =
      useState<SimpleBarComponent | null>(null);

    useEffect(() => {
      let cancelled = false;
      loadSimpleBar()
        .then((mod) => {
          if (cancelled) return;
          setSimpleBarComp(() => mod.default);
        })
        .catch(() => {
          /* fallback natif reste en place */
        });
      return () => {
        cancelled = true;
      };
    }, []);

    if (SimpleBarComp) {
      return (
        <SimpleBarComp
          style={style}
          className={className}
          {...(rest as SimpleBarProps)}
          ref={ref as React.Ref<any>}
        >
          {children}
        </SimpleBarComp>
      );
    }

    // Fallback natif : même contrat DOM minimal (conteneur scrollable) que SimpleBar.
    return (
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        className={className}
        style={{ overflow: "auto", ...style }}
      >
        {children}
      </div>
    );
  }
);

LazySimpleBar.displayName = "LazySimpleBar";

export default LazySimpleBar;
